import React from 'react';
import { Brain, TrendingUp, TrendingDown, AlertTriangle, Clock, Shield } from 'lucide-react';

const riskColors = {
  critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', bar: 'bg-red-500' },
  high: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', bar: 'bg-orange-500' },
  medium: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', bar: 'bg-yellow-500' },
  low: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', bar: 'bg-green-500' },
};

const BreachPredictionCard = ({ prediction, covenantName, loanName, compact = false }) => {
  const { probability, risk, confidence, timeToBreatch, factors } = prediction;
  const colors = riskColors[risk] || riskColors.low;

  if (compact) {
    return (
      <div className={`flex items-center gap-3 p-3 rounded-lg ${colors.bg} ${colors.border} border`}>
        <Brain className={`w-5 h-5 ${colors.text}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">{covenantName}</p>
          <p className="text-xs text-slate-500">{loanName}</p>
        </div>
        <div className="text-right">
          <p className={`text-lg font-bold ${colors.text}`}>{Math.round(probability * 100)}%</p>
          <p className="text-xs text-slate-500">risk</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl ${colors.bg} ${colors.border} border p-5`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${colors.bar} bg-opacity-20 flex items-center justify-center`}>
            <Brain className={`w-5 h-5 ${colors.text}`} />
          </div>
          <div>
            <h4 className="font-semibold text-slate-800">{covenantName}</h4>
            <p className="text-sm text-slate-500">{loanName}</p>
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}>
          {risk.toUpperCase()}
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-600">Breach Probability</span>
          <span className={`text-xl font-bold ${colors.text}`}>{Math.round(probability * 100)}%</span>
        </div>
        <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${colors.bar} transition-all duration-500`}
            style={{ width: `${probability * 100}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <Shield className="w-4 h-4 text-slate-400" />
          <span className="text-slate-600">Confidence:</span>
          <span className="font-medium">{Math.round(confidence * 100)}%</span>
        </div>
        {timeToBreatch && (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-slate-400" />
            <span className="text-slate-600">ETA:</span>
            <span className="font-medium">{timeToBreatch} period{timeToBreatch > 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {factors && (
        <div className="pt-3 border-t border-slate-200">
          <p className="text-xs font-medium text-slate-500 mb-2">RISK FACTORS</p>
          <div className="grid grid-cols-2 gap-2">
            <FactorBar label="Trend" value={factors.trend} icon={factors.trend > 0.5 ? TrendingUp : TrendingDown} />
            <FactorBar label="Headroom" value={factors.headroom} icon={AlertTriangle} />
            <FactorBar label="Volatility" value={factors.volatility} icon={TrendingUp} />
            <FactorBar label="History" value={factors.historical} icon={Clock} />
          </div>
        </div>
      )}
    </div>
  );
};

const FactorBar = ({ label, value, icon: Icon }) => {
  const percentage = Math.round(value * 100);
  const color = value > 0.6 ? 'bg-red-400' : value > 0.3 ? 'bg-yellow-400' : 'bg-green-400';

  return (
    <div className="flex items-center gap-2">
      <Icon className="w-3 h-3 text-slate-400" />
      <span className="text-xs text-slate-600 w-16">{label}</span>
      <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};

export default BreachPredictionCard;